import { DEFAULT_VALUES } from 'src/apps/dashboard/constants/BusinessUnitsTable';
import { IBusinessUnitRow, IScoreData } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { BusinessUnit } from 'src/apps/dashboard/types/DashboardTypes';

import { BusinessUnitsTableDataQuery } from '../BusinessUnitsTable.generated';

export interface BusinessUnitsTableResult {
	rows: IBusinessUnitRow[];
	total: number;
	unfilteredTotal: number;
	scoreWebProducts: ScoreWebProduct[];
	aggregatedScoreWebProducts: AggregatedScoreWebProducts;
	aggregatedScoreDeltas: AggregatedScoreDeltaResults;
}

export type ScoreWebProduct = NonNullable<
	NonNullable<BusinessUnitsTableDataQuery['score_web_products']>[number]
>;

type GroupedScoreWebProducts = Record<string, Record<string, Record<string, ScoreWebProduct[]>>>;

type AggregatedScoreEntry = {
	calculationDate: string;
	unitId: string;
	timeframeDays: string;
	totalUniqueIssues: number;
	weightedScore: number;
};

export type AggregatedScoreWebProducts = AggregatedScoreEntry[];

type AggregatedScoreDeltaEntry = {
	unitId: string;
	timeframeDays: string;
	latestCalculationDate: string;
	previousCalculationDate: string;
	latestScore: number;
	previousScore: number;
	deltaScore: number;
};

export type AggregatedScoreDeltaResults = AggregatedScoreDeltaEntry[];

const DEFAULT_SCORE_DATA: IScoreData = { value: 0, percentage: 0 };
const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

const UNKNOWN_CALCULATION_DATE_KEY = 'unknown';

interface IssueResult {
	filtered_custom_fields?: Array<string | null> | null;
	issue_id_count?: number;
	critical_issues_sum?: number;
	serious_issues_sum?: number;
}

interface UnitScoreMetrics {
	uniqueIssues?: number;
	weightedScore?: number;
	deltaScore?: number;
}

const extractScoreWebProducts = (
	scoreWebProducts?: BusinessUnitsTableDataQuery['score_web_products']
): ScoreWebProduct[] =>
	scoreWebProducts?.filter((product): product is ScoreWebProduct => product !== null) ?? [];

const groupScoreWebProducts = (scoreWebProducts: ScoreWebProduct[]): GroupedScoreWebProducts => {
	const grouped: GroupedScoreWebProducts = {};

	scoreWebProducts.forEach((product) => {
		const unitId = product.unit_id?.trim();
		if (!unitId) {
			return;
		}

		const dateKey = product.calculation_date ?? UNKNOWN_CALCULATION_DATE_KEY;
		const timeframeKey = String(product.timeframe_days ?? 0);

		if (!grouped[dateKey]) {
			grouped[dateKey] = {};
		}

		if (!grouped[dateKey][timeframeKey]) {
			grouped[dateKey][timeframeKey] = {};
		}

		if (!grouped[dateKey][timeframeKey][unitId]) {
			grouped[dateKey][timeframeKey][unitId] = [];
		}

		grouped[dateKey][timeframeKey][unitId].push(product);
	});

	return grouped;
};

const aggregateGroupedScoreWebProducts = (
	groupedScoreWebProducts: GroupedScoreWebProducts
): AggregatedScoreWebProducts => {
	const aggregated: AggregatedScoreWebProducts = [];

	Object.entries(groupedScoreWebProducts).forEach(([dateKey, timeframeGroups]) => {
		Object.entries(timeframeGroups).forEach(([timeframeKey, unitGroups]) => {
			Object.entries(unitGroups).forEach(([unitId, products]) => {
				const totalUniqueIssues = products.reduce(
					(sum, product) => sum + (product.unique_issues ?? 0),
					0
				);
				const weightedScoreNumerator = products.reduce(
					(sum, product) => sum + (product.score ?? 0) * (product.unique_issues ?? 0),
					0
				);
				const weightedScore =
					totalUniqueIssues > 0 ? weightedScoreNumerator / totalUniqueIssues : 0;

				aggregated.push({
					calculationDate: dateKey,
					unitId,
					timeframeDays: timeframeKey,
					totalUniqueIssues,
					weightedScore
				});
			});
		});
	});

	return aggregated;
};

const getDateValue = (dateString: string): number => {
	const timestamp = Date.parse(dateString);
	return Number.isNaN(timestamp) ? 0 : timestamp;
};

const calculateDeltaScoreEntries = (
	aggregatedScores: AggregatedScoreWebProducts
): AggregatedScoreDeltaResults => {
	const groupedByUnitAndTimeframe: Record<string, AggregatedScoreEntry[]> = {};

	aggregatedScores.forEach((entry) => {
		const key = `${entry.unitId}__${entry.timeframeDays}`;
		if (!groupedByUnitAndTimeframe[key]) {
			groupedByUnitAndTimeframe[key] = [];
		}
		groupedByUnitAndTimeframe[key].push(entry);
	});

	return Object.values(groupedByUnitAndTimeframe)
		.map((entries) => {
			if (entries.length < 2) {
				return undefined;
			}

			const sortedEntries = [...entries].sort(
				(a, b) => getDateValue(a.calculationDate) - getDateValue(b.calculationDate)
			);
			const latest = sortedEntries[sortedEntries.length - 1];
			let previous: AggregatedScoreEntry | undefined;

			for (let index = sortedEntries.length - 2; index >= 0; index -= 1) {
				if (sortedEntries[index].calculationDate !== latest.calculationDate) {
					previous = sortedEntries[index];
					break;
				}
			}

			if (!latest || !previous) {
				return undefined;
			}

			const latestScore = latest.weightedScore ?? 0;
			const previousScore = previous.weightedScore ?? 0;

			return {
				unitId: latest.unitId,
				timeframeDays: latest.timeframeDays,
				latestCalculationDate: latest.calculationDate,
				previousCalculationDate: previous.calculationDate,
				latestScore,
				previousScore,
				deltaScore: latestScore - previousScore
			};
		})
		.filter(
			(entry): entry is AggregatedScoreDeltaEntry =>
				Boolean(entry) && entry.latestCalculationDate !== entry.previousCalculationDate
		);
};

const buildUnitScoreMetrics = (
	aggregatedEntries: AggregatedScoreWebProducts,
	aggregatedDeltas: AggregatedScoreDeltaResults,
	timeframeKey: string
): Map<string, UnitScoreMetrics> => {
	const latestEntryByUnit = new Map<string, AggregatedScoreEntry>();
	aggregatedEntries
		.filter((entry) => entry.timeframeDays === timeframeKey)
		.forEach((entry) => {
			const existing = latestEntryByUnit.get(entry.unitId);
			if (
				!existing ||
				getDateValue(entry.calculationDate) > getDateValue(existing.calculationDate)
			) {
				latestEntryByUnit.set(entry.unitId, entry);
			}
		});

	const deltaByUnit = new Map<string, AggregatedScoreDeltaEntry>();
	aggregatedDeltas
		.filter((delta) => delta.timeframeDays === timeframeKey)
		.forEach((delta) => deltaByUnit.set(delta.unitId, delta));

	const unitMetrics = new Map<string, UnitScoreMetrics>();
	latestEntryByUnit.forEach((entry, unitId) => {
		const delta = deltaByUnit.get(unitId);
		unitMetrics.set(unitId, {
			uniqueIssues: entry.totalUniqueIssues,
			weightedScore: entry.weightedScore,
			deltaScore: delta?.deltaScore
		});
	});

	return unitMetrics;
};

const formatScoreData = (weightedScore?: number, deltaScore?: number): IScoreData => {
	if (weightedScore === undefined && deltaScore === undefined) {
		return DEFAULT_SCORE_DATA;
	}

	const scoreValue =
		weightedScore !== undefined ? roundToOneDecimal(Math.max(weightedScore * 100, 0)) : 0;
	const scorePercentage =
		deltaScore !== undefined ? roundToOneDecimal(deltaScore * 100) : DEFAULT_SCORE_DATA.percentage;

	return {
		value: scoreValue,
		percentage: scorePercentage
	};
};

const processSumResults = (
	issueResults: IssueResult[] | null | undefined,
	criticalIssuesMap: Map<string, number>,
	seriousIssuesMap: Map<string, number>,
	filteredUnitIds?: Set<string>
): void => {
	if (!issueResults) {
		return;
	}

	issueResults.forEach((item) => {
		const criticalSum = item.critical_issues_sum ?? 0;
		const seriousSum = item.serious_issues_sum ?? 0;

		// If there's a filter applied, only process unitIds that match the filter
		if (filteredUnitIds && filteredUnitIds.size > 0) {
			const validUnitIds =
				item.filtered_custom_fields?.filter(
					(field): field is string =>
						field !== null && field !== undefined && filteredUnitIds.has(field)
				) ?? [];

			// Process each valid unitId
			validUnitIds.forEach((unitId) => {
				const existingCritical = criticalIssuesMap.get(unitId) ?? 0;
				const existingSerious = seriousIssuesMap.get(unitId) ?? 0;

				criticalIssuesMap.set(unitId, existingCritical + criticalSum);
				seriousIssuesMap.set(unitId, existingSerious + seriousSum);
			});
		} else {
			// No filter: use original behavior - take the first unitId
			const unitId = item.filtered_custom_fields?.[0];
			if (unitId) {
				const existingCritical = criticalIssuesMap.get(unitId) ?? 0;
				const existingSerious = seriousIssuesMap.get(unitId) ?? 0;

				criticalIssuesMap.set(unitId, existingCritical + criticalSum);
				seriousIssuesMap.set(unitId, existingSerious + seriousSum);
			}
		}
	});
};

const createBusinessUnitRow = (
	unit: BusinessUnit,
	criticalIssues: number,
	seriousIssues: number,
	unitMetrics?: UnitScoreMetrics
): IBusinessUnitRow => {
	const uniqueIssues =
		unitMetrics?.uniqueIssues !== undefined
			? unitMetrics.uniqueIssues
			: criticalIssues + seriousIssues;
	const score = formatScoreData(unitMetrics?.weightedScore, unitMetrics?.deltaScore);

	return {
		id: unit.id,
		name: unit.name,
		criticalIssues,
		seriousIssues,
		uniqueIssues,
		score,
		// Add flat numeric field for sorting
		scoreValue: score.value
	};
};

export const transformBusinessUnitsTableResults = (
	rawResults: BusinessUnitsTableDataQuery,
	businessUnits: BusinessUnit[],
	filteredUnitIds?: Set<string>,
	selectedPeriodDays?: number
): BusinessUnitsTableResult => {
	const scoreWebProducts = extractScoreWebProducts(rawResults?.score_web_products);
	const groupedScoreWebProducts = groupScoreWebProducts(scoreWebProducts);
	const aggregatedScoreWebProducts = aggregateGroupedScoreWebProducts(groupedScoreWebProducts);
	const aggregatedScoreDeltas = calculateDeltaScoreEntries(aggregatedScoreWebProducts);
	const criticalIssuesMap = new Map<string, number>();
	const seriousIssuesMap = new Map<string, number>();
	const timeframeKey = String(selectedPeriodDays ?? DEFAULT_VALUES.SELECTED_PERIOD);
	const unitScoreMetrics = buildUnitScoreMetrics(
		aggregatedScoreWebProducts,
		aggregatedScoreDeltas,
		timeframeKey
	);

	// Process Web SDK issues (both critical and serious)
	// Only filter unitIds if a filter is explicitly applied
	processSumResults(
		rawResults?.wsdkIssuesCount,
		criticalIssuesMap,
		seriousIssuesMap,
		filteredUnitIds
	);
	// Process WFA issues (both critical and serious)
	processSumResults(
		rawResults?.wfaIssuesCount,
		criticalIssuesMap,
		seriousIssuesMap,
		filteredUnitIds
	);

	const unitsWithData = new Set<string>();
	[...criticalIssuesMap.keys(), ...seriousIssuesMap.keys(), ...unitScoreMetrics.keys()].forEach(
		(unitId) => {
			unitsWithData.add(unitId);
		}
	);

	const rows: IBusinessUnitRow[] = businessUnits
		.filter((unit) => unitsWithData.has(unit.id))
		.map((unit) => {
			const criticalIssues = criticalIssuesMap.get(unit.id) ?? 0;
			const seriousIssues = seriousIssuesMap.get(unit.id) ?? 0;

			return createBusinessUnitRow(
				unit,
				criticalIssues,
				seriousIssues,
				unitScoreMetrics.get(unit.id)
			);
		});

	return {
		rows,
		total: rows.length,
		unfilteredTotal: rows.length,
		scoreWebProducts,
		aggregatedScoreWebProducts,
		aggregatedScoreDeltas
	};
};
